import { useState, type ComponentType } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import {
  HeartPulse,
  Activity,
  TrendingUp,
  Network,
  Cloud,
  Bluetooth,
  Layers,
  Users,
  Building2,
  Home,
  ShieldCheck,
  Workflow,
  Droplet,
  Thermometer,
  Wind,
  HeartHandshake,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Navigation from "@/components/landing/Navigation";
import Footer from "@/components/landing/Footer";
import Vida24ContactModal, { InquiryType } from "@/components/Vida24ContactModal";
import { useTranslation } from "@/hooks/useTranslation";
import heroImage from "@/assets/vida24-hero.png";

type LucideIcon = ComponentType<{ className?: string }>;

const DeviceIconAnimated = ({ index, Icon }: { index: number; Icon: LucideIcon }) => {
  const cls = "w-7 h-7";
  switch (index) {
    case 0:
      // Glucose - droplet falling
      return (
        <motion.div
          animate={{ y: [-6, 4, 4], opacity: [0, 1, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeIn", times: [0, 0.6, 1] }}
        >
          <Icon className={cls} />
        </motion.div>
      );
    case 1:
      // Blood pressure - heartbeat pulse (double-beat)
      return (
        <motion.div
          animate={{ scale: [1, 1.18, 1, 1.12, 1] }}
          transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut", times: [0, 0.15, 0.35, 0.5, 1] }}
        >
          <Icon className={cls} />
        </motion.div>
      );
    case 2:
      // Thermometer - temperature rising
      return (
        <motion.div
          animate={{ y: [0, -3, 0], color: ["hsl(var(--secondary))", "#ef4444", "hsl(var(--secondary))"] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <Icon className={cls} />
        </motion.div>
      );
    case 3:
      // Spirometer - blowing
      return (
        <motion.div
          animate={{ x: [-3, 4, -3], opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        >
          <Icon className={cls} />
        </motion.div>
      );
    case 4:
      // ECG - line drawing
      return <Icon className={`${cls} animate-ecg-draw`} />;
    default:
      return <Icon className={cls} />;
  }
};

const Vida24HomeCare = () => {
  const { t } = useTranslation();
  const tr = t.vida24HomeCare;
  const [modalOpen, setModalOpen] = useState(false);
  const [inquiryType, setInquiryType] = useState<InquiryType>("Demo Request");
  const [audienceIdx, setAudienceIdx] = useState(0);

  const benefitIcons = [Home, Activity, TrendingUp, Network];
  const deviceIcons = [Droplet, HeartPulse, Thermometer, Wind, Activity];
  const capabilityIcons = [Cloud, Workflow, Bluetooth];
  const audienceIcons = [Users, Building2, HeartHandshake];
  const reasonIcons = [ShieldCheck, Bluetooth, Layers, TrendingUp];

  const benefits = tr.benefits.map((b, i) => ({ ...b, icon: benefitIcons[i] }));
  const steps = tr.howItWorks.steps;
  const devices = tr.devices.items.map((d, i) => ({ ...d, icon: deviceIcons[i] }));
  const capabilities = tr.capabilities.items.map((c, i) => ({ ...c, icon: capabilityIcons[i] }));
  const audiences = tr.audiences.items.map((a, i) => ({ ...a, icon: audienceIcons[i] }));
  const reasons = tr.reasons.items.map((r, i) => ({ ...r, icon: reasonIcons[i] }));

  useEffect(() => {
    const t = setInterval(() => setAudienceIdx((i) => (i + 1) % audiences.length), 5000);
    return () => clearInterval(t);
  }, [audiences.length]);

  const openWith = (type: InquiryType) => {
    setInquiryType(type);
    setModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-100 to-neutral-50 relative overflow-hidden">
      <Helmet>
        <title>{tr.meta.title}</title>
        <meta name="description" content={tr.meta.description} />
        <link rel="canonical" href="https://vidavo.eu/about/vida24-home-care" />
        <meta property="og:title" content={tr.meta.ogTitle} />
        <meta property="og:description" content={tr.meta.ogDescription} />
        <meta property="og:url" content="https://vidavo.eu/about/vida24-home-care" />
      </Helmet>

      <Navigation />

      {/* HERO */}
      <header className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-[60%] bg-gradient-to-bl from-secondary/15 via-secondary/8 to-transparent blur-3xl" />
        <div className="absolute top-1/4 left-0 w-1/3 h-[50%] bg-gradient-to-r from-accent-teal/12 via-accent-teal/6 to-transparent blur-2xl" />

        <div className="container-padding relative z-10">
          <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, type: "spring", stiffness: 400, damping: 30 }}
            >
              <div className="mb-6">
                <svg width="160" height="68" viewBox="0 0 347 147" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M219.803 18.9681C234.277 5.67277 254.262 -1.22722 275.41 0.179953H275.44H275.469C276.79 0.267294 278.102 0.39346 279.403 0.548734C298.895 2.83903 315.952 12.2331 328.069 25.8973C340.985 40.4639 348.251 59.8829 346.822 80.6897V80.7188V80.7479C346.743 81.9124 346.634 83.077 346.496 84.2318L346.447 84.62C346.102 87.4441 345.609 90.2196 344.958 92.9272C344.002 96.8964 342.74 100.749 341.172 104.476C333.137 123.574 317.303 138.985 296.933 146.429C295.474 146.963 293.877 146.235 293.344 144.808C292.802 143.391 293.542 141.809 294.991 141.276C313.872 134.376 328.552 120.091 336.006 102.37C337.455 98.9247 338.628 95.3534 339.506 91.6753C340.393 87.9779 340.975 84.193 341.251 80.3597V80.3306V80.3015C342.572 61.0087 335.838 43.0065 323.859 29.5074C311.89 15.9985 294.685 6.9829 275.075 5.66307H275.045H275.006C255.415 4.35294 237.55 11.2627 223.836 23.0441C198.793 46.7622 200.942 72.1205 201.396 89.4432C201.139 92.0829 196.614 92.3741 196.269 89.6762C194.691 53.2741 203.151 34.7673 219.803 18.9681ZM213.385 71.7323C214.459 56.2243 221.588 42.628 232.611 33.1563C243.633 23.6846 258.324 18.3567 274.059 19.4048H274.099L274.128 19.4145C289.874 20.4723 303.697 27.712 313.319 38.5618C322.942 49.4116 328.355 63.8715 327.29 79.3698V79.399L327.28 79.4184C327.152 81.3593 326.916 83.2808 326.58 85.1829C326.245 87.0947 325.811 88.9677 325.299 90.8019C322.597 100.274 317.401 108.95 310.145 115.908C309.326 116.684 309.317 117.975 310.105 118.78C310.894 119.586 312.205 119.596 313.024 118.819C320.803 111.366 326.363 102.05 329.272 91.8791C329.834 89.8994 330.297 87.8905 330.652 85.8719C331.007 83.8437 331.264 81.7863 331.402 79.7095V79.6804V79.6513C332.545 63.0369 326.738 47.5289 316.425 35.893C306.112 24.2572 291.294 16.4934 274.424 15.358L274.385 15.3483H274.355C257.476 14.2323 241.721 19.9483 229.899 30.0994C218.078 40.2504 210.19 54.8365 209.027 71.4509L209.037 71.48C208.988 74.0032 213.316 73.8285 213.385 71.7323ZM241.731 43.4918C250.081 36.3298 261.203 32.2344 273.123 33.0302H273.152L273.192 33.0399C285.112 33.8356 295.582 39.3188 302.869 47.5386C310.145 55.7584 314.256 66.7053 313.448 78.4382V78.477V78.4964C313.379 79.4669 313.28 80.4082 313.152 81.3399C313.034 82.2424 312.866 83.1741 312.659 84.1445C311.673 88.9095 309.849 93.4804 307.286 97.6631C306.94 98.2259 307.128 98.9635 307.709 99.3032C308.281 99.6428 309.031 99.4585 309.376 98.8956C312.087 94.48 314 89.647 315.045 84.62C315.242 83.6787 315.42 82.6888 315.558 81.6601C315.696 80.6606 315.804 79.661 315.873 78.6711V78.642V78.6129C316.721 66.22 312.383 54.6521 304.693 45.9664C296.992 37.2905 285.95 31.4968 273.359 30.6525L273.3 30.6428C260.7 29.8082 249 34.4187 240 41.9187C231.5 50.9187 229.17 56.7289 226.212 64.3373C225.601 66.0745 228.41 66.8994 229.032 65.0361C230.925 57.8546 238.329 46.3158 241.731 43.4918Z" fill="#008791"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M283.998 131.668L288.78 131.484C297.338 131.377 297.308 117.674 288.711 117.82L284.196 117.693L284.067 112.346C283.634 105.708 272.068 104.359 270.649 112.346V117.761H261.095L288.849 89.7925C293.601 85.998 287.035 75.3035 279.552 79.7482L246.365 112.472C235.855 121.342 241.435 131.183 247.607 131.416L270.718 131.668V140.431C271.654 147.137 283.781 146.817 284.136 140.431L283.998 131.668Z" fill="#F36F21"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M241.199 145.72C250.752 144.73 250.496 132.017 241.199 131.542H223.915L243.161 112.346C254.114 98.5558 244.58 85.4157 241.199 83.7368C233.755 78.3702 221.667 76.5457 211.216 86.2503C205.616 92.0925 204.916 103.67 211.354 109.124C216.54 114.083 225.66 108.93 221.825 102.554C216.432 95.4891 221.973 90.0933 227.78 90.6368C234.603 91.7723 236.525 98.517 232.365 103.389L206.967 128.514C198.784 137.025 205.261 145.012 210.694 145.72H241.199Z" fill="#F36F21"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M192.457 118.69C192.41 111.349 184.507 95.7998 166.779 95.306C161.309 95.0451 140.306 98.9021 140.23 122.612C140.723 134.015 151.238 147.486 166.817 146.927C173.736 147.216 176.197 134.593 166.713 134.145C162.217 134.341 156.699 131.425 154.409 126.441C151.191 119.416 155.175 108.731 166.864 108.302C170.187 108.293 174.569 110.184 176.547 113.053C180.503 118.774 179.225 122.137 179.453 131.816L179.49 140.806C180.001 149.051 192.457 148.79 192.571 140.806L192.457 118.69Z" fill="#008791"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M107.217 108.228C115.821 108.516 117.572 96.1723 107.586 95.427C88.562 95.6227 80.7441 112.569 81.5392 122.64C82.6749 139.81 98.7272 147.672 107.87 146.955C120.07 147.188 133.151 137.527 133.927 122.612L133.87 88.7007C133.028 81.4434 121.736 81.0148 120.884 88.8032L120.818 122.64C118.83 134.415 106.441 136.717 99.0963 130.847C91.3541 124.671 93.5878 109.215 107.217 108.228Z" fill="#008791"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M12.91 100.784C10.6479 92.4833 0.690929 94.7005 0 100.784C0.160901 130.186 18.8538 145.614 26.0755 146.927C35.5781 145.316 52.8892 125.957 52.1793 100.784C50.6555 93.7782 40.7459 93.4708 39.2694 100.784C38.9665 113.296 34.4234 125.491 26.1133 131.816C19.2514 127.298 13.2791 115.094 12.91 100.784Z" fill="#008791"/>
                  <path fillRule="evenodd" clipRule="evenodd" d="M74.8288 100.784C73.3523 93.471 63.2345 93.7225 61.9094 100.784L61.8242 140.807C62.2596 149.257 74.9235 148.455 74.9045 140.807L74.8288 100.784Z" fill="#008791"/>
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold leading-tight text-primary mb-6">
                <span className="block text-[0.7em] font-normal opacity-80 mb-2">
                  {tr.hero.titlePrefix}
                </span>
                <span className="block">
                  {tr.hero.titleItems.map((item, i) => {
                    const colors = ["text-secondary", "text-accent-orange", "text-primary"];
                    return (
                      <span
                        key={i}
                        className={`block ${colors[i % colors.length]} animate-fade-up opacity-0`}
                        style={{ animationDelay: `${i * 150}ms` }}
                      >
                        {item}
                      </span>
                    );
                  })}
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-700 leading-relaxed mb-8">
                {tr.hero.subtitle}
              </p>
              <button
                type="button"
                onClick={() => openWith("Demo Request")}
                className="button-primary inline-flex items-center gap-2"
              >
                {tr.hero.requestDemo}
                <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative flex items-center justify-center"
            >
              {/* Decorative ambient glow behind the floating devices */}
              <div className="absolute inset-0 -z-10 flex items-center justify-center">
                <div className="w-[90%] h-[70%] rounded-full bg-gradient-to-tr from-secondary/25 via-accent-teal/20 to-transparent blur-3xl" />
              </div>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-primary/20 blur-2xl rounded-full -z-10" />
              <motion.img
                src={heroImage}
                alt="Vida24 telemonitoring tablet, desktop dashboard and smartphone"
                className="w-full max-w-2xl object-contain drop-shadow-2xl"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              />
            </motion.div>
          </div>
        </div>
      </header>

      {/* MAIN BENEFITS */}
      <section className="py-16 md:py-20 relative">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((b, i) => (
                <motion.div
                  key={b.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  whileHover={{ scale: 1.03 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-neutral-100"
                >
                  <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-accent-teal/20 to-secondary/20 text-secondary mb-4">
                    <b.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-primary mb-2">{b.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{b.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Roadmap Timeline */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="container-padding relative z-10">
          <div className="text-center mb-16">
            <motion.h2
              className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent-teal bg-clip-text text-transparent mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {tr.howItWorks.title}
            </motion.h2>
            <div className="w-20 h-1 bg-gradient-to-r from-accent-teal to-primary rounded-full mx-auto" />
          </div>

          <div className="max-w-5xl mx-auto relative">
            {/* Central Timeline Line */}
            <div className="absolute left-1/2 top-0 bottom-0 transform -translate-x-1/2 hidden md:block">
              <div className="relative w-1 h-full">
                <div className="absolute inset-0 w-px bg-gradient-to-b from-transparent via-secondary/60 to-transparent ml-0.5" />
              </div>
            </div>

            {steps.map((s, i) => {
              const position = i % 2 === 0 ? "left" : "right";
              const colors = [
                { solid: "#A9E0DD", shadow: "shadow-[#A9E0DD]/20" },
                { solid: "#66B3BB", shadow: "shadow-[#66B3BB]/20" },
                { solid: "#C8DDFF", shadow: "shadow-[#C8DDFF]/20" },
                { solid: "#868EAD", shadow: "shadow-[#868EAD]/20" },
                { solid: "#B4C3FD", shadow: "shadow-[#B4C3FD]/20" },
              ];
              return (
                <motion.div
                  key={s.title}
                  className={`relative z-10 mb-16 flex items-center md:items-start ${position === "right" ? "md:flex-row-reverse" : ""} flex-col md:flex-row`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.7, delay: i * 0.12, ease: [0.25, 0.4, 0.25, 1] }}
                >
                  {/* Timeline Dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 hidden md:block z-20">
                    <motion.div
                      className="w-5 h-5 bg-white rounded-full border-4 shadow-lg border-secondary/40"
                      animate={{
                        scale: [1, 1.15, 1],
                        boxShadow: [
                          `0 0 0 0 hsl(184, 100%, 28%, 0.3)`,
                          `0 0 0 10px hsl(184, 100%, 28%, 0.1)`,
                          `0 0 0 0 hsl(184, 100%, 28%, 0.3)`,
                        ],
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.3, ease: [0.4, 0, 0.6, 1] }}
                    />
                  </div>

                  {/* Step Number (mobile only) */}
                  <div className="md:hidden mb-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent-teal to-secondary text-white font-bold flex items-center justify-center shadow-md">
                      {i + 1}
                    </div>
                    <span className="text-sm font-semibold text-secondary uppercase tracking-wider">{`${i + 1}`}</span>
                  </div>

                  {/* Content Card */}
                  <motion.div
                    className={`flex-1 max-w-md ${position === "right" ? "md:mr-12" : "md:ml-12"} mb-8 md:mb-0`}
                    whileHover={{ y: -4, transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] } }}
                  >
                    <div
                      className="backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/20 hover:shadow-2xl transition-all duration-500 ease-out relative z-10"
                      style={{ backgroundColor: colors[i].solid }}
                    >
                      {/* Step number badge (desktop) */}
                      <div className="hidden md:inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/30 shadow-sm mb-5">
                        <span className="text-xl font-bold text-slate-800">{i + 1}</span>
                      </div>

                      <h3 className="text-2xl font-bold mb-3 leading-tight text-slate-800">
                        {s.title}
                      </h3>
                      <p className="leading-relaxed text-lg text-slate-700">
                        {s.text}
                      </p>
                    </div>
                  </motion.div>

                  {/* Spacer for the other side */}
                  <div className="hidden md:block flex-1 max-w-md" />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DEVICES */}
      <section className="py-16 md:py-20">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">{tr.devices.title}</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                {tr.devices.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {devices.map((d, i) => (
                <motion.div
                  key={d.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  whileHover={{ scale: 1.04 }}
                  className="bg-white rounded-2xl p-5 shadow-md border border-neutral-100 text-center"
                >
                  <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-accent-teal/15 to-secondary/15 text-secondary mb-3 overflow-hidden">
                    <DeviceIconAnimated index={i} Icon={d.icon} />
                  </div>
                  <div className="font-semibold text-primary text-sm">{d.name}</div>
                  <div className="text-xs text-neutral-500 mt-1">{d.detail}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white to-neutral-100/50">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">{tr.capabilities.title}</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {capabilities.map((c, i) => (
                <motion.div
                  key={c.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-neutral-100"
                >
                  <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-accent-teal/20 to-secondary/20 text-secondary mb-4">
                    <c.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-lg font-bold text-primary mb-2">{c.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{c.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 md:py-20">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-primary mb-4">{tr.audiences.title}</h2>
              <p className="text-neutral-600 max-w-2xl mx-auto">
                {tr.audiences.subtitle}
              </p>
            </div>
            <div className="relative max-w-4xl mx-auto">
              <div className="relative bg-gradient-to-br from-secondary to-primary rounded-[2.5rem] p-8 md:p-12 lg:p-16 text-white shadow-2xl overflow-hidden min-h-[380px]"
                style={{ boxShadow: "0 25px 50px -10px hsla(184, 100%, 28%, 0.4)" }}
              >
                <button
                  onClick={() => setAudienceIdx((i) => (i - 1 + audiences.length) % audiences.length)}
                  aria-label={tr.audiences.prev}
                  className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full text-white hover:bg-white/15 transition"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setAudienceIdx((i) => (i + 1) % audiences.length)}
                  aria-label={tr.audiences.next}
                  className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 p-2 rounded-full text-white hover:bg-white/15 transition"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="px-8 md:px-12">
                  <AnimatePresence mode="wait">
                    {(() => {
                      const a = audiences[audienceIdx];
                      const Icon = a.icon;
                      return (
                        <motion.div
                          key={a.title}
                          initial={{ opacity: 0, x: 40 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -40 }}
                          transition={{ duration: 0.4, type: "spring", stiffness: 400, damping: 30 }}
                          className="text-center"
                        >
                          <div className="inline-flex p-4 rounded-2xl bg-white/15 backdrop-blur-sm mb-5 shadow-lg">
                            <Icon className="w-8 h-8" />
                          </div>
                          <h3 className="text-2xl md:text-3xl font-bold mb-6">{a.title}</h3>
                          <ul className="space-y-3 max-w-xl mx-auto text-left">
                            {a.items.map((it) => (
                              <li key={it} className="flex items-start gap-3 text-base md:text-lg opacity-95">
                                <ArrowRight className="w-5 h-5 mt-1 flex-shrink-0 text-white/80" />
                                <span>{it}</span>
                              </li>
                            ))}
                          </ul>
                        </motion.div>
                      );
                    })()}
                  </AnimatePresence>
                </div>

                <div className="flex justify-center gap-2 mt-8">
                  {audiences.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setAudienceIdx(i)}
                      aria-label={`${tr.audiences.slide} ${i + 1}`}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === audienceIdx ? "bg-white w-8" : "bg-white/40 hover:bg-white/60 w-2"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY VIDA24 */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-white to-neutral-100/50">
        <div className="container-padding">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-primary via-secondary to-accent-teal bg-clip-text text-transparent mb-4 leading-[1.3] pb-2">
                {tr.reasons.title}
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-accent-teal to-primary rounded-full mx-auto" />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {reasons.map((r, i) => (
                <motion.div
                  key={r.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  whileHover={{ scale: 1.03 }}
                  className="bg-white rounded-2xl p-6 shadow-md border border-neutral-100"
                >
                  <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-accent-teal/20 to-secondary/20 text-secondary mb-4">
                    <r.icon className="w-6 h-6" />
                  </div>
                  <h3 className="text-base font-bold text-primary mb-2">{r.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{r.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container-padding">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="w-24 h-1 bg-gradient-to-r from-accent-teal to-secondary rounded-full" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">{tr.cta.title}</h2>
            <p className="text-neutral-600 mb-8">
              {tr.cta.subtitle}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => openWith("Demo Request")} className="button-primary">
                {tr.cta.demo}
              </button>
              <button type="button" onClick={() => openWith("Pilot Program")} className="button-primary">
                {tr.cta.pilot}
              </button>
              <button type="button" onClick={() => openWith("General Inquiry")} className="button-primary">
                {tr.cta.contact}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <Vida24ContactModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        inquiryType={inquiryType}
      />
    </div>
  );
};

export default Vida24HomeCare;